import { isAppShellIconKey, type AppShellIconKey } from "../../../../../../shared/frontend/layout/appShellIcons";



import { reportsIconKeyForPath, reportsLabelKeyForPath } from "../../pages/reports/reportsCatalog";
import { settingsIconKeyForPath, settingsTitleForPath } from "../../pages/settings/settingsCatalog";
import { toolsIconKeyForPath, toolsTitleForPath } from "../../pages/tools/toolsCatalog";
import { computeMenuSelection, findMenuRouteByPath, normalizeMenuPath } from "../layout/menu";
import type { MenuRoute } from "../layout/menu";
import { memberAdminCatalog } from "../layout/adminNavigationCatalog";
import { ROUTE_ROOT } from "../constants/appRoutes";

/**
 * Beschriftung und Icon einer pinbaren Seite.
 *
 * Beides wird bewusst erst beim Rendern aufgelöst statt im Pin gespeichert: So folgen Pins
 * automatisch der gewählten Sprache, umbenannten Menüpunkten und entzogenen Rechten
 * (kein Treffer mehr => Pin ist ungültig und wird entfernt).
 */

/** Admin-Unterseiten mit eigenem Kopf (aus `AppLayout` ausgelagert, damit Kopf und Pins dieselbe Quelle nutzen). */
export const ADMIN_SETTINGS_TITLE_BY_PATH: Record<string, string> = Object.fromEntries(
  memberAdminCatalog.entries.filter(entry => entry.path.startsWith("/admin/settings/")).map(entry => [entry.path, entry.titleKey])
);

export const ADMIN_IMPORT_TITLE_BY_PATH: Record<string, string> = {
  "/admin/import/schemas": "Mapping templates",
  "/admin/import/runs": "Mapping imports",
  "/admin/import/adapters": "Adapter imports",
  "/admin/import/merges": "Merge imports"
};

export const ADMIN_GENERAL_TITLE_BY_PATH: Record<string, string> = {
  "/admin/settings": "Admin Settings",
  "/admin/tenant-area": "Tenants",
  ...Object.fromEntries(memberAdminCatalog.entries.filter(entry => !entry.path.startsWith("/admin/settings/")).map(entry => [entry.path, entry.titleKey]))
};

/**
 * i18n-Key für den Seitentitel einer Unterseite ohne eigenen Menüeintrag.
 * Deckt Einstellungen, Werkzeuge, Auswertungen und die Admin-Bereiche ab.
 * Wie überall nur der Seitenname; den Bereich nennt der Zurück-Link.
 */
export function resolvePageTitleKey(pathname: string): string | null {
  const path = normalizeMenuPath(pathname);
  if (!path) return null;
  return (
    ADMIN_IMPORT_TITLE_BY_PATH[path] ??
    ADMIN_SETTINGS_TITLE_BY_PATH[path] ??
    ADMIN_GENERAL_TITLE_BY_PATH[path] ??
    settingsTitleForPath(path) ??
    toolsTitleForPath(path) ??
    reportsLabelKeyForPath(path) ??
    null
  );
}

/**
 * Symbol der Seite selbst, aus demselben Katalog wie ihre Kachel.
 *
 * Ohne das trügen alle Einstellungs-Pins dasselbe Zahnrad (das Icon des Menü-Vorfahren) und
 * wären nur noch am Text unterscheidbar. Admin-Unterseiten pflegen ihre Symbole bislang in den
 * Seitenkomponenten selbst und fallen deshalb weiter auf das Bereichssymbol zurück.
 */
function resolvePageIcon(path: string): AppShellIconKey | null {
  const settingsKey = settingsIconKeyForPath(path);
  if (settingsKey) return isAppShellIconKey(settingsKey) ? settingsKey : null;

  const toolsKey = toolsIconKeyForPath(path);
  if (toolsKey) return isAppShellIconKey(toolsKey) ? toolsKey : null;

  const reportsKey = reportsIconKeyForPath(path);
  if (reportsKey) return isAppShellIconKey(reportsKey) ? reportsKey : "BarChartOutlined";

  return null;
}

/** Nächstgelegener Menü-Vorfahre (z. B. „Einstellungen“ für alle Einstellungs-Unterseiten). */
function ancestorMenuRoute(menu: MenuRoute[], path: string): MenuRoute | null {
  const selectedKey = computeMenuSelection(path, menu).selectedKeys[0];
  if (!selectedKey) return null;
  return findMenuRouteByPath(menu, selectedKey);
}

/** Personenakte: wird über den Pin-Button in der Akte selbst gepinnt (mit Name statt Routentitel). */
const PERSON_DETAIL_PATH = /^\/persons\/(?!members$|prospects$|former$)[^/]+$/;

export function isPersonDetailPath(path: string): boolean {
  return PERSON_DETAIL_PATH.test(normalizeMenuPath(path));
}

export type PinDisplay = {
  label: string;
  icon: AppShellIconKey;
  /**
   * Zusatz für den Tooltip. Gesetzt, wenn die Beschriftung vom Menü-Vorfahren geerbt ist und
   * deshalb für sich allein nicht eindeutig wäre (zwei Unterseiten desselben Bereichs).
   */
  detail?: string;
};

/**
 * Beschriftung + Icon für einen Routen-Pin. `null` = Seite ist (nicht mehr) pinbar,
 * etwa weil der Bereich durch fehlende Rechte aus dem Menü verschwunden ist.
 *
 * Auflösung in vier Stufen, damit praktisch jede Arbeitsseite pinbar ist:
 * exakter Menüeintrag → Seitentitel aus den Katalogen → Menü-Vorfahre (+ Pfad im Tooltip) → nicht pinbar.
 */
export function resolveRoutePinDisplay(args: {
  menu: MenuRoute[];
  path: string;
  tr: (key: string) => string;
}): PinDisplay | null {
  const path = normalizeMenuPath(args.path);
  if (!path || path === ROUTE_ROOT) return null;

  const menuRoute = findMenuRouteByPath(args.menu, path);
  if (menuRoute) {
    return { label: menuRoute.name, icon: menuRoute.icon ?? "LinkOutlined" };
  }

  const ancestor = ancestorMenuRoute(args.menu, path);

  const titleKey = resolvePageTitleKey(path);
  if (titleKey) {
    return { label: args.tr(titleKey), icon: resolvePageIcon(path) ?? ancestor?.icon ?? "LinkOutlined" };
  }

  if (ancestor) {
    return { label: ancestor.name, icon: ancestor.icon ?? "LinkOutlined", detail: path };
  }

  // Letzte Rückfallebene: Seiten ohne Menübezug bleiben pinbar, damit der Pin-Knopf
  // auf JEDER Seite dieselbe Stelle hat. Beschriftung aus dem letzten Pfadsegment.
  const lastSegment = path.split("/").filter(Boolean).pop();
  if (!lastSegment) return null;
  return { label: lastSegment.replace(/[-_]/g, " "), icon: "LinkOutlined", detail: path };
}
